import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Checkbox from '@axa-japan/design-system-react/CheckboxGroupField';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon } from '@axa-japan/design-system-react/Icons';
import Text from '@axa-japan/design-system-react/Text';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import ErrorMessage from '../components/ErrorMessage';
import CustomModal from '../components/common/CustomModal';
import useCreateUploadAction from '../hooks/useCreateUploadAction';
import { useGetDocuments } from '../hooks/useGetDocuments';
import useInvalidateAccess from '../hooks/useInvalidateAccess';
import { queryClient } from '../libs/queryClient';
import { useUserContext } from '../providers/UserProvider';
import { DOCUMENT_STATUS } from '../types/session';
import { FILE_DEFAULT_NAME } from '../utils/fileUtils';
import { DocumentType } from '../utils/formUtils';
import { CheckboxContainer } from './AgreementPage';
import PaginatedPreview from './PaginatedPreview';

type UploadValidationPageForm = {
  sessionId?: number;
  documentsSelected: string[];
  pendingUploadDocuments: DocumentType[];
  failedUploadDocuments: DocumentType[];
  uploadConfirmation: boolean;
};

const UploadValidationPageSchema = z.object({
  uploadConfirmation: z.boolean().refine((value) => value === true, 'チェックしてください。'),
});

type UploadValidationPageProps = {
  initialData: UploadValidationPageForm;
  onNext: (data: UploadValidationPageForm, freezeStepper?: boolean) => void;
  onBack: () => void;
};

const UploadValidationPage: React.FC<UploadValidationPageProps> = ({ onBack, onNext, initialData }) => {
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<UploadValidationPageForm>({
    resolver: zodResolver(UploadValidationPageSchema),
    mode: 'onSubmit',
    defaultValues: initialData,
  });

  const { setBulkProcessing } = useUserContext();

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isPartialErrorModalOpen, setIsPartialErrorModalOpen] = useState(false);
  const [documentFailedList, setDocumentFailedList] = useState<(DocumentType | undefined)[]>();

  const postRequest = useCreateUploadAction();
  const invalidatePreviousAccess = useInvalidateAccess();

  const uploadingDocuments = initialData.pendingUploadDocuments.map((doc) => {
    return {
      ...doc,
      files: doc.files.filter((file) => file?.content?.name !== FILE_DEFAULT_NAME),
    };
  });
  const { data: documents } = useGetDocuments(initialData.sessionId as number);
  const latestRequestedDocuments =
    documents?.filter((item) => item.documentStatus === DOCUMENT_STATUS.urlGenerated || item.documentStatus === DOCUMENT_STATUS.smsSent) ?? [];

  const isOverrideRequestedDocuments = latestRequestedDocuments.some((document) =>
    uploadingDocuments.some((selected) => selected.documentId === document.id),
  );

  useEffect(() => {
    setValue('uploadConfirmation', false);
  }, [setValue]);

  const handleConfirmSubmit: SubmitHandler<UploadValidationPageForm> = async (formData) => {
    queryClient.invalidateQueries({ queryKey: ['session', initialData.sessionId, 'documents'] });
    onNext(formData);
  };

  const onSubmit: SubmitHandler<UploadValidationPageForm> = async () => {
    try {
      // Show global loader
      setBulkProcessing(true);

      if (isOverrideRequestedDocuments) {
        await invalidatePreviousAccess.mutateAsync({
          sessionId: Number(initialData.sessionId),
          formIds: uploadingDocuments.map((doc) => doc.documentId),
        });
      }
      const documentUploadPromiseMap = uploadingDocuments.map(async (document) => {
        return postRequest.mutateAsync({
          sessionId: initialData.sessionId as number,
          document,
        });
      });

      const results = await Promise.all(documentUploadPromiseMap);
      const documentsFailedIds = results.filter((result) => result.error).map((result) => result.document?.documentId);

      setDocumentFailedList(results.filter((result) => result.error).map((result) => result.document));

      if (documentsFailedIds.length === 0) {
        setIsSuccessModalOpen(true);
      } else {
        const { documentsSelected } = initialData;

        const allSelectedInErrors =
          documentsSelected.length === documentsFailedIds.length && documentsSelected.every((doc) => documentsFailedIds.includes(doc));
        const singleSelectedInErrors = documentsSelected.length === 1 && documentsFailedIds.includes(documentsSelected[0]);

        allSelectedInErrors || singleSelectedInErrors ? setIsErrorModalOpen(true) : setIsPartialErrorModalOpen(true);

        setValue('failedUploadDocuments', [
          // merge 2 list and make a new list of failed upload documents.
          ...uploadingDocuments.filter((document) => documentsFailedIds.includes(document.documentId)),
          ...initialData.failedUploadDocuments.filter((doc) => !documentsFailedIds.includes(doc.documentId)),
        ]);
        setValue('pendingUploadDocuments', []);

        setValue('documentsSelected', []);
      }
    } finally {
      // Hide global loader
      setBulkProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10">
      <CustomModal
        open={isSuccessModalOpen}
        title="画像送信が完了しました"
        onClose={() => handleConfirmSubmit({ ...getValues(), pendingUploadDocuments: [], documentsSelected: [] })}
        buttonLabel="撮影方法選択画面へ"
      >
        <>
          <Text>画像の送信が完了しました。</Text>
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </>
      </CustomModal>
      <CustomModal
        open={isErrorModalOpen}
        title="送信に失敗しました"
        onClose={() => handleConfirmSubmit(getValues())}
        buttonLabel="撮影方法選択画面へ"
        isWarningModal
      >
        <>
          <Text>致命的なエラーが発生し、以下の帳票の送信に失敗しました。</Text>
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </>
      </CustomModal>
      <CustomModal
        open={isPartialErrorModalOpen}
        title="一部の送信に失敗しました"
        onClose={() => handleConfirmSubmit(getValues())}
        buttonLabel="撮影方法選択画面へ"
        isWarningModal
      >
        <div className="flex flex-col gap-5">
          <Text>エラーが発生し、以下の書類の送信に失敗しました。</Text>
          {documentFailedList?.map((documentFailed) => (
            <Text color="danger" key={`document-failed-waring${documentFailed?.documentId}`}>
              ・{documentFailed?.name}
            </Text>
          ))}
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </div>
      </CustomModal>

      <Heading as="h1">撮影画像の確認</Heading>
      {uploadingDocuments.map((document, index) => {
        return (
          <div key={document.documentId} className="flex flex-col py-8">
            <div>
              <Heading as="h2" className="pb-4">
                {index + 1}.{document.name}
              </Heading>
            </div>
            <PaginatedPreview {...document} />
          </div>
        );
      })}

      <div>
        <div className="flex w-full flex-row items-center gap-2">
          <Heading as="h3">確認してください</Heading>
          <Badge variant="important" text="必須" />
        </div>
        <ol className="bg-interactiveHoverGrey mb-4 p-2">
          <li>・複数ページ、裏面がある書類はすべてのページを撮影していますか？</li>
          <li>・画像が暗い、ブレている等で、文字が読めないことはありませんか？</li>
          <li>・帳票名と撮影画像に相違はないですか？</li>
          <li>・不要な画像があれば前の画面に戻り削除してください。</li>
          <li>・容量サイズが大きい場合は、送信に多くの時間を要することをご了承ください。</li>
        </ol>
        <CheckboxContainer checked={watch('uploadConfirmation')}>
          <Controller
            name={'uploadConfirmation'}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Checkbox
                showError={Boolean(errors['uploadConfirmation'])}
                required={true}
                onChange={field.onChange}
                options={[
                  {
                    defaultChecked: field.value,
                    label: '上記の内容を確認しました。',
                    value: 'uploadConfirmation',
                  },
                ]}
              />
            )}
          />
        </CheckboxContainer>
        {errors['uploadConfirmation'] && <ErrorMessage message={String(errors['uploadConfirmation']?.message)} />}
      </div>

      <div className="flex justify-center gap-3 mt-5">
        <div className="w-80">
          <Button variant="outlined" onClick={() => onBack()} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
            撮影し直す
          </Button>
        </div>
        <div className="w-80">
          <Button type="submit" fullWidth icon={<ArrowIcon />}>
            画像を送信する
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UploadValidationPage;
